/**
 * Breadcrumb sink for session-termination and session-suppression events.
 *
 * Deliberately import-free from `AuthService` (would be circular: the guard,
 * the boot initializer AND the error interceptor all need to log, and all
 * three sit "above" or "beside" `AuthService` in the dependency graph).
 *
 * Sink is a single `console.warn` — Gleap already captures console output in
 * production, so these breadcrumbs reach bug reports for free, with zero new
 * infrastructure.
 *
 * NEVER pass access/refresh tokens, `Authorization` headers, email, name,
 * identification, or request bodies in a `SessionEvent`. `userId`, if
 * present, must be an opaque identifier only.
 */

export type SessionEventReason =
	| 'guard.redirect.no-token'
	| 'guard.redirect.refresh-failed'
	| 'interceptor.logout.direct'
	| 'interceptor.logout.refresh-failed'
	| 'interceptor.skip.superseded'
	| 'interceptor.skip.retry-failed'
	| 'interceptor.skip.forbidden'
	| 'bootstrap.logout.refresh-failed'
	| 'user.logout.explicit'
	| 'auth.login.session-reset'
	| 'auth.xsrf.missing';

export interface SessionEvent {
	reason: SessionEventReason;
	/** ISO timestamp of when the event was logged. */
	at: string;
	/** `sessionEpoch` at log time. */
	epoch?: number;
	/** Dispatch-time epoch of the originating request (interceptor only). */
	requestEpoch?: number;
	/** URL path ONLY — query string must be stripped by the caller. */
	path?: string;
	status?: number;
	/** Opaque identifier only — never an email, name, or other PII. */
	userId?: string;
}

export function logSessionEvent(event: SessionEvent): void {
	console.warn('[session]', event);
}
